import React, { useEffect } from 'react';
import { Banner, Link } from '@airbus/components-react';
import { useLanguageContextAPI } from '../context/LanguageContext';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import styles from './ReleaseBanner.module.css';

const validVariants = ['info', 'warning', 'error', 'success'] as const;

type BannerPayload = {
  variant: (typeof validVariants)[number];
  autoHideDuration: number;
  message: {
    en: string;
    de: string;
  };
  link: string;
};

function isPayloadValid(payload: BannerPayload, enabled: boolean, language: string): boolean {
  const { message, variant } = payload || {};
  return (
    // feature enabled
    enabled &&
    // payload has message in correct format
    message &&
    typeof message === 'object' &&
    // message has supported language [en, de]
    message?.[language] &&
    // variant is valid or not
    (variant === undefined || validVariants.includes(variant))
  );
}

export default function ReleaseBanner() {
  const { t, language } = useLanguageContextAPI();
  // releaseBanner PostHog feature flag
  const releaseBannerEnabled = useFeatureFlagEnabled('releaseBanner');
  const releaseBannerPayload = useFeatureFlagPayload('releaseBanner') as BannerPayload;
  // Banner state
  const [openBanner, setOpenBanner] = React.useState(false);

  useEffect(() => {
    if (isPayloadValid(releaseBannerPayload, releaseBannerEnabled, language)) {
      setOpenBanner(true);
    } else setOpenBanner(false);
  }, [releaseBannerEnabled, releaseBannerPayload]);

  return (
    <>
      {openBanner && (
        <div className={styles.releaseBannerScope}>
          <Banner
            variant={releaseBannerPayload?.variant || 'info'}
            onClose={() => setOpenBanner(false)}
            {...(releaseBannerPayload?.autoHideDuration && {
              autoHideDuration: releaseBannerPayload.autoHideDuration,
            })}
          >
            <div className={styles.bannerInline}>
              <span>
                {t(releaseBannerPayload?.message?.[`${language}`] ?? 'banner.info_message')}
              </span>
              {releaseBannerPayload?.link && (
                <Link href={releaseBannerPayload.link} negative size='small' target='_blank'>
                  {t('banner.click_here')}
                </Link>
              )}
            </div>
          </Banner>
        </div>
      )}
    </>
  );
}
